var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import React from 'react';
import { BannerType } from './Types';
import Text from '../Text';
import ActionButton from '../ActionButton';
import { isButtonProps } from '../Button/utils';
import classNames from 'classnames';
import IconButton from '../IconButton';
import useKeyboardEventHandler from '../../hooks/useKeyboardEventHandler';
import Badge from '../Badge';
import getConfig from './helper';
var STYLE_NAMESPACE = 'vcp-banner';
var DEFAULT_SIZE = (_a = {},
    _a[BannerType.Light] = 'small',
    _a[BannerType.Dark] = 'small',
    _a[BannerType.Card] = 'medium',
    _a);
var useButtonsToRender = function (_a) {
    var onClick = _a.onClick, primaryButton = _a.primaryButton, secondaryButton = _a.secondaryButton, type = _a.type, isDisabled = _a.isDisabled, config = _a.config;
    var isPropMainOnClickExist = !!onClick;
    var isNotCardType = type !== BannerType.Card;
    var isPrimaryButtonProp = isButtonProps(primaryButton);
    var primaryButtonPropOnClick = isPrimaryButtonProp && (primaryButton === null || primaryButton === void 0 ? void 0 : primaryButton.onClick);
    var isSecondaryButtonProp = isButtonProps(secondaryButton);
    var secondaryButtonPropOnClick = isSecondaryButtonProp && (secondaryButton === null || secondaryButton === void 0 ? void 0 : secondaryButton.onClick);
    var primaryClick = primaryButtonPropOnClick && !secondaryButtonPropOnClick
        ? primaryButtonPropOnClick
        : null;
    var secondaryClick = !primaryButtonPropOnClick && secondaryButtonPropOnClick
        ? secondaryButtonPropOnClick
        : null;
    var isOnlyOneOnClickExist = primaryClick || secondaryClick;
    var isInteractive = (isPropMainOnClickExist || isOnlyOneOnClickExist) && !isDisabled;
    var mainBannerClickHandler = isInteractive
        ? onClick || primaryClick || secondaryClick
        : undefined;
    var PrimaryButtonToRender = React.useMemo(function () {
        var isValidReact = React.isValidElement(primaryButton);
        var isValidProps = primaryButtonPropOnClick;
        return isValidReact ? (primaryButton) : isValidProps ? (React.createElement(BannerButton, { config: config.button[1], props: primaryButton })) : null;
    }, [primaryButton, type, config]);
    var SecondaryButtonToRender = React.useMemo(function () {
        var isValidReact = React.isValidElement(secondaryButton) && isNotCardType;
        var isValidProps = secondaryButtonPropOnClick && isNotCardType;
        return isValidReact ? (secondaryButton) : isValidProps ? (React.createElement(BannerButton, { config: config.button[0], props: secondaryButton })) : null;
    }, [primaryButton, type, config]);
    return {
        mainBannerClickHandler: mainBannerClickHandler,
        isInteractive: isInteractive,
        PrimaryButtonToRender: PrimaryButtonToRender,
        SecondaryButtonToRender: SecondaryButtonToRender,
    };
};
var Banner = function (_a) {
    var _b;
    var onClose = _a.onClose, primaryText = _a.primaryText, secondaryText = _a.secondaryText, textVerticalAlignment = _a.textVerticalAlignment, textHorizontalAlignment = _a.textHorizontalAlignment, primaryButton = _a.primaryButton, secondaryButton = _a.secondaryButton, _c = _a.type, type = _c === void 0 ? BannerType.Light : _c, backgroundImageUrl = _a.backgroundImageUrl, color = _a.color, badgeText = _a.badgeText, badgeStyle = _a.badgeStyle, sizeProp = _a.size, isLoading = _a.isLoading, isDisabled = _a.isDisabled, onClick = _a.onClick;
    var size = sizeProp || DEFAULT_SIZE[type];
    var config = React.useMemo(function () { return getConfig(type, size); }, [type, size]);
    var _d = useButtonsToRender({
        onClick: onClick,
        primaryButton: primaryButton,
        secondaryButton: secondaryButton,
        type: type,
        isDisabled: isDisabled,
        config: config,
    }), mainBannerClickHandler = _d.mainBannerClickHandler, isInteractive = _d.isInteractive, PrimaryButtonToRender = _d.PrimaryButtonToRender, SecondaryButtonToRender = _d.SecondaryButtonToRender;
    var imageStyle = React.useMemo(function () { return ({
        backgroundImage: "url(".concat(backgroundImageUrl, ")"),
        backgroundColor: color,
    }); }, [backgroundImageUrl, color]);
    var onBannerClick = React.useCallback(function (e) {
        e.stopPropagation();
        mainBannerClickHandler === null || mainBannerClickHandler === void 0 ? void 0 : mainBannerClickHandler(e);
    }, [mainBannerClickHandler]);
    var handleEnterDown = useKeyboardEventHandler('Enter', onBannerClick);
    var handleCloseButtonOnClick = React.useCallback(function (e) {
        e.stopPropagation();
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }, [onClose]);
    var textComponents = React.useMemo(function () {
        return config.textOrder.map(function (textKey) {
            var textValue = textKey === 'primaryText' ? primaryText : secondaryText;
            var textConfig = textKey === 'primaryText' ? config.text[1] : config.text[0];
            return textValue && React.createElement(BannerText, { config: textConfig, text: textValue });
        });
    }, [primaryText, secondaryText, config.text, config.textOrder]);
    return (React.createElement("div", { className: classNames(STYLE_NAMESPACE, "".concat(STYLE_NAMESPACE, "-").concat(type), "".concat(STYLE_NAMESPACE, "-").concat(size), "".concat(STYLE_NAMESPACE, "-text-vertical-").concat(textVerticalAlignment), "".concat(STYLE_NAMESPACE, "-text-horizontal-").concat(textHorizontalAlignment), (_b = {},
            _b["".concat(STYLE_NAMESPACE, "-is-interactive")] = isInteractive,
            _b["".concat(STYLE_NAMESPACE, "-loading")] = isLoading,
            _b["".concat(STYLE_NAMESPACE, "-disabled")] = isDisabled,
            _b)), onClick: onBannerClick, onKeyDown: handleEnterDown, tabIndex: isInteractive ? 0 : -1, role: isInteractive ? 'button' : undefined, style: !isLoading ? imageStyle : undefined }, isLoading ? (React.createElement("div", { className: "".concat(STYLE_NAMESPACE, "-loading-container ").concat(size, " ").concat(type) },
        React.createElement("div", { className: "".concat(STYLE_NAMESPACE, "-loading-text") }),
        React.createElement("div", { className: "".concat(STYLE_NAMESPACE, "-loading-button") }))) : (React.createElement(React.Fragment, null,
        onClose && (React.createElement("div", { className: "".concat(STYLE_NAMESPACE, "-close-container") },
            React.createElement(IconButton, { iconName: "close", onClick: handleCloseButtonOnClick, buttonType: config.close }))),
        badgeText && (React.createElement(Badge, { size: "small", styleMode: badgeStyle }, badgeText)),
        React.createElement("div", { className: "".concat(STYLE_NAMESPACE, "-text-container ").concat(type) }, textComponents),
        !isDisabled && (React.createElement("div", { className: "".concat(STYLE_NAMESPACE, "-buttons-container ").concat(badgeText && 'badge') },
            SecondaryButtonToRender,
            PrimaryButtonToRender))))));
};
export default Banner;
export var BannerText = function (_a) {
    var config = _a.config, text = _a.text;
    return (React.createElement(Text, __assign({}, config), text));
};
export var BannerButton = function (_a) {
    var config = _a.config, props = _a.props;
    return (React.createElement(ActionButton, __assign({}, config, props), props.value));
};
